import React, { useState, useEffect } from 'react';
import theme from './ChangeRequestDetailsOverridesTab.module.scss';
import { Trans } from '@lingui/react/macro';
import { ChangeRequest, BuyerOptionGroupOverride, OptionGroupDto, RoomDto } from '@/types';
import { NoResultsCard } from '@/components/Cards';
import { isChangeRequestLocked } from '.';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import ChangeRequestOverrideOptionDialog from '../ChangeRequestOverrideOptionDialog';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import { faEraser } from '@fortawesome/pro-light-svg-icons';
import { searchOptionGroups } from '@/services/options/option-groups';
import { getRooms } from '@/services/rooms';
import { RoomPill } from '@/seller/components/options/Rooms/RoomPill';
import { OptionGroupAccordion } from '@/seller/components/options/OptionGroupAccordion/OptionGroupAccordion';

interface ChangeRequestDetailsOverridesTabProps {
    changeRequest: ChangeRequest;
    onChange(newOverrides: BuyerOptionGroupOverride[]): void;
}

const ChangeRequestDetailsOverridesTab: React.FC<ChangeRequestDetailsOverridesTabProps> = ({ changeRequest, onChange }) => {
    const isLocked = isChangeRequestLocked(changeRequest);
    const [showOverrideOptionsDialog, setShowOverrideOptionsDialog] = useState(false);
    // const [optionGroups] = useAsyncState(
    //     () =>
    //         searchBuyerOptionGroups({ residentialUnitId: changeRequest.residentialUnitId }).then((result) =>
    //             result.filter((group) => group.overrideInfo.some((x) => x.changeRequestId === changeRequest.id))
    //         ),
    //     [changeRequest.residentialUnitId],
    //     null
    // );

    const [optionGroups, setOptionGroups] = useState<OptionGroupDto[] | null>(null);
    const [roomsById, setRoomsById] = useState<Record<number, RoomDto>>({});

    useEffect(() => {
        async function load() {
            const [result, rooms] = await Promise.all([
                searchOptionGroups({
                    unitIds: [changeRequest.residentialUnitId],
                    projectId: changeRequest.projectId,
                }),
                getRooms(changeRequest.projectId),
            ]);
            const overriddenGroupIds = new Set(changeRequest.optionGroupOverrides.map((x) => x.optionGroupId));
            const relevantGroups = result.filter((group) => overriddenGroupIds.has(group.id));

            setOptionGroups(relevantGroups);
            setRoomsById(Object.fromEntries(rooms.map((r) => [r.id, r])));
        }

        load();
    }, [changeRequest.optionGroupOverrides, changeRequest.residentialUnitId]);

    if (!optionGroups) {
        return <LoadingIndicator />;
    }

    return (
        <>
            {!isLocked && (
                <ButtonBar margins="bottom">
                    <ButtonBar.Right>
                        <Button
                            onClick={() => setShowOverrideOptionsDialog(true)}
                            icon={faEraser}
                            variant="tertiary"
                            disabled={isLocked}>
                            <Trans>Edit overrides</Trans>
                        </Button>
                    </ButtonBar.Right>

                    <ChangeRequestOverrideOptionDialog
                        changeRequest={changeRequest}
                        show={showOverrideOptionsDialog}
                        onClose={() => setShowOverrideOptionsDialog(false)}
                        onSave={onChange}
                    />
                </ButtonBar>
            )}

            {optionGroups.length === 0 ? (
                <NoResultsCard>
                    <Trans>This change request does not override any option groups</Trans>
                </NoResultsCard>
            ) : (
                <div className={theme.list}>
                    {optionGroups.map((group) => {
                        const overriddenRoomIds = changeRequest.optionGroupOverrides
                            .filter((x) => x.optionGroupId === group.id && x.roomId !== null)
                            .map((x) => x.roomId);
                        return (
                            <div key={group.id} className={theme.listItem}>
                                {overriddenRoomIds.length > 0 && (
                                    <div className={theme.overrideRow}>
                                        <span className={theme.overrideLabel}>
                                            <Trans>Overridden rooms</Trans>
                                        </span>
                                        {overriddenRoomIds.map((id) => {
                                            const room = roomsById[id!];
                                            return room ? (
                                                <RoomPill key={id} room={room} />
                                            ) : (
                                                <span key={id}>{`Room ${id}`}</span>
                                            );
                                        })}
                                    </div>
                                )}
                                <OptionGroupAccordion accordionVariant="bordered" readonly optionGroup={group} />
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

export default ChangeRequestDetailsOverridesTab;
